"use client";

import { Icon, Text } from "@zapier/design-system";
import { type Template } from "@zapier/templates";

import { useRouter } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import styles from "./UseCases.module.css";
import { Category } from "src/server/zsl/templates/types";
import classNames from "classnames";

const cardWidth = 220;
const gapWidth = 30;
const scrollWidth = cardWidth + gapWidth;

interface UseCaseProps {
  templateCount: number;
  title: string;
  slug: string;
}

const UseCase = ({ slug, templateCount, title }: UseCaseProps) => {
  const router = useRouter();

  return (
    <div
      className={styles.card}
      onClick={() => router.push(`/templates/${slug}`)}
      role="button"
      onKeyDown={(e) => e.key === "Enter" && router.push(`/templates/${slug}`)}
      tabIndex={-1}
    >
      <div>
        <Text type="SectionHeader">{title}</Text>
      </div>
      <div>
        <Text type="SmallPrint1" tag="p">
          {templateCount} template{templateCount > 1 ? "s" : ""}
        </Text>
      </div>
    </div>
  );
};

interface UseCasesProps {
  categories: Category[];
  templates: Template[];
}

export const UseCases = ({ categories, templates }: UseCasesProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [leftScrollable, setLeftScrollable] = useState<boolean>(false);
  const [rightScrollable, setRightScrollable] = useState<boolean>(true);

  useEffect(() => {
    const currentContainer = scrollRef.current;
    const checkScrollPosition = () => {
      if (scrollRef.current) {
        const container = scrollRef.current;
        setLeftScrollable(container.scrollLeft !== 0);
        setRightScrollable(
          container.scrollLeft + container.clientWidth < container.scrollWidth,
        );
      }
    };
    checkScrollPosition();
    currentContainer?.addEventListener("scroll", checkScrollPosition);

    return () => {
      currentContainer?.removeEventListener("scroll", checkScrollPosition);
    };
  }, []);

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft -= scrollWidth; // Adjust this value based on card width + margin
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += scrollWidth; // Adjust this value based on card width + margin
    }
  };

  return (
    <div className={styles.wrapper}>
      {leftScrollable ? (
        <button
          className={classNames([styles.scrollButton, styles.scrollButtonLeft])}
          onClick={handleScrollLeft}
        >
          <Icon name="arrowSmallLeft" color="PrimeBlack" size={24} />
        </button>
      ) : null}
      {rightScrollable ? (
        <button className={styles.scrollButton} onClick={handleScrollRight}>
          <Icon name="arrowSmallRight" color="PrimeBlack" size={24} />
        </button>
      ) : null}
      <div className={styles.cards}>
        <div className={styles.scrollableContainer} ref={scrollRef}>
          {categories.map((category) => {
            const count = templates.filter((t) => {
              return t.categories?.items.some((c) => c.slug === category.slug);
            }).length;

            return (
              <UseCase
                key={category.slug}
                title={category.name}
                templateCount={count}
                slug={category.slug}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
